/**
 * TodayPageBackdrop — POL-6 / POL-10 / POL-12.
 *
 * Sky-band behind the top of the Today route.  After POL-12 the
 * gradient no longer stretches the whole page — Today now matches
 * every other page in the app (Clients, Projects, Inquiries, …)
 * which paint a header band that hard-cuts into editorial black
 * below.  The moon + starfield live INSIDE the band; below ~32vh
 * the page is solid `TODAY_PAGE_FLOOR` so cards read cleanly.
 *
 * What changed in POL-12:
 *   • Gradient stops repacked into a top-only band:
 *     0vh (color0) → 12vh (color1) → 22vh (color2) → 32vh (floor).
 *     Everything below 32vh renders as solid `TODAY_PAGE_FLOOR`.
 *   • Star region shrunk from 130vh → 28vh (slightly less than
 *     the gradient band so stars don't overlap the fade edge).
 *   • Star count re-tuned so density INSIDE the smaller band roughly
 *     matches the old page-wide density — night ≈ 90 (was 130),
 *     twilight 30, sunset 15, sunrise 8, morning 4, midday 0.
 *     Overridden locally instead of reading `g.starCount` so the
 *     GLASS-1 parallel task can retune `todayTimeOfDay.ts` freely
 *     without touching web polish.
 *
 * Prior context (POL-10):
 *   • Star shapes — 5 families (star5 / star4 / star6 / sparkle /
 *     dot), weighted so `star5` renders ~2× (classic sky default).
 *     Big/hero stars are 6–10px, biased toward star5; small stars
 *     are 2–5px.
 *   • Twinkle animation — opacity+scale pulse (reduced-motion aware).
 *   • Moon layer — night-only, top-right of the viewport (~10vh
 *     down, ~6vw from the right edge), sits inside the gradient
 *     band by design.
 */
import { useMemo } from 'react';
import {
  bucketFromHour,
  gradientFor,
  TODAY_PAGE_FLOOR,
  type TimeOfDay,
} from '@/lib/todayTimeOfDay';
// POL-11e — light mode short-circuits the whole sky.  We render a
// plain near-white surface instead so Today stays editorial and
// legible in light mode (no gradient, no stars, no moon).
import { useThemeMode } from '@/hooks/useThemeMode';

export interface TodayPageBackdropProps {
  timeOfDay?: TimeOfDay;
  className?: string;
}

// ─── Star shape vocabulary ──────────────────────────────────────
type StarShape = 'star4' | 'star5' | 'star6' | 'sparkle' | 'dot';

// star5 repeats so 5-point stars are the most common — feels closest
// to the classic "sky" default.  Other shapes sprinkle in as accents.
const SHAPES: StarShape[] = ['star5', 'star4', 'star6', 'sparkle', 'star5', 'dot', 'star5'];

interface StarSpec {
  x: number;   // %
  y: number;   // vh (0..maxYvh)
  size: number; // px
  shape: StarShape;
  delay: number;
  duration: number;
  opacity: number;
}

function seededStars(count: number, seed: number, maxYvh: number): StarSpec[] {
  let s = seed >>> 0;
  const rand = () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
  const stars: StarSpec[] = [];
  for (let i = 0; i < count; i++) {
    // Size distribution: mostly small (2–5px), a handful "hero"
    // (6–10px). Match the retired hero backdrop's numbers exactly.
    const bigStar = rand() < 0.18;
    const size = bigStar ? 6 + rand() * 4 : 2 + rand() * 3;
    // Bias big stars toward classic 5-point so they read as "stars"
    // and not random sparkles.
    const shape: StarShape = bigStar
      ? rand() < 0.6
        ? 'star5'
        : SHAPES[Math.floor(rand() * SHAPES.length)]
      : SHAPES[Math.floor(rand() * SHAPES.length)];
    stars.push({
      x: rand() * 100,
      y: rand() * maxYvh,
      size,
      shape,
      delay: rand() * 5,
      duration: 2.5 + rand() * 2.5,
      opacity: 0.45 + rand() * 0.55,
    });
  }
  return stars;
}

// ─── Star shape SVG paths ───────────────────────────────────────
// Each star renders into a square viewBox 0..10 (5,5 = center) so
// it stays 1:1 regardless of container aspect.  Ported from the
// retired TodayHeroBackdrop.

function starPolygonPoints(points: number, outer: number, inner: number, cx = 5, cy = 5): string {
  const pts: string[] = [];
  const totalPoints = points * 2;
  for (let i = 0; i < totalPoints; i++) {
    const angle = (Math.PI / points) * i - Math.PI / 2;
    const r = i % 2 === 0 ? outer : inner;
    pts.push(`${(cx + Math.cos(angle) * r).toFixed(2)},${(cy + Math.sin(angle) * r).toFixed(2)}`);
  }
  return pts.join(' ');
}

function ShapeSvg({ shape, color, opacity }: { shape: StarShape; color: string; opacity: number }) {
  const commonProps = { fill: color, opacity };
  switch (shape) {
    case 'star4':
      return <polygon {...commonProps} points={starPolygonPoints(4, 4.5, 1.2)} />;
    case 'star5':
      return <polygon {...commonProps} points={starPolygonPoints(5, 4.5, 1.8)} />;
    case 'star6':
      return <polygon {...commonProps} points={starPolygonPoints(6, 4.5, 2)} />;
    case 'sparkle':
      return (
        <g {...commonProps}>
          <ellipse cx={5} cy={5} rx={4.5} ry={0.7} />
          <ellipse cx={5} cy={5} rx={0.7} ry={4.5} />
        </g>
      );
    case 'dot':
    default:
      return <circle cx={5} cy={5} r={2} {...commonProps} />;
  }
}

// ─── Moon phase ─────────────────────────────────────────────────
// Reference new moon: Jan 6 2000 18:14 UTC. Synodic cycle 29.53d.
// Returns phase 0..1 (0 = new, 0.5 = full, 1 = new again).
function lunarPhase(now: Date): number {
  const referenceNewMoon = Date.UTC(2000, 0, 6, 18, 14);
  const synodicMs = 29.53058867 * 24 * 60 * 60 * 1000;
  const diff = now.getTime() - referenceNewMoon;
  const mod = ((diff % synodicMs) + synodicMs) % synodicMs;
  return mod / synodicMs;
}

function Moon({
  phase,
  size = 44,
  color = 'rgba(255, 244, 220, 0.95)',
}: {
  phase: number;
  size?: number;
  color?: string;
}) {
  const radius = size / 2;
  const shadowOffset = (0.5 - phase) * 2 * size;
  const maskId = `today-moon-mask-${Math.floor(phase * 10000)}`;
  const glowId = `today-moon-glow-${Math.floor(phase * 10000)}`;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <defs>
        <radialGradient id={glowId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="70%" stopColor={color} stopOpacity="0" />
        </radialGradient>
        <mask id={maskId}>
          <rect width={size} height={size} fill="black" />
          <circle cx={radius} cy={radius} r={radius - 1} fill="white" />
          <circle cx={radius + shadowOffset} cy={radius} r={radius - 1} fill="black" />
        </mask>
      </defs>
      <circle cx={radius} cy={radius} r={radius} fill={`url(#${glowId})`} />
      <circle cx={radius} cy={radius} r={radius - 1} fill={color} mask={`url(#${maskId})`} />
    </svg>
  );
}

// POL-12 — star counts overridden locally per bucket.  The palette
// in `todayTimeOfDay.ts` still owns colors + moon toggle, but the
// star density is now tuned for the tighter (28vh) top-band region.
// Keeping the override here means GLASS-1 (parallel task tweaking
// morning/midday color values) can retune palettes without touching
// the star density knob.
const POL12_STAR_COUNTS: Record<TimeOfDay, number> = {
  night:    90,
  twilight: 30,
  sunset:   15,
  sunrise:   8,
  morning:   4,
  midday:    0,
};

// POL-15 — smoother, darker fade.  The POL-12 hard cut at 32vh was
// too abrupt (bright sunrise top → pitch black in 10vh looked bad).
// New curve overlays a darker mid-tone at ~55% of the fade region so
// bright palettes (sunrise, sunset) bleed cleanly into the editorial
// black instead of chopping off.  Sky owns the top ~40vh, then a
// long soft transition through an intermediate dark stop, landing
// fully on floor by 75vh.  Everything below 75vh stays solid.
function pol15Darken(hex: string): string {
  // Blend the palette stop 55% toward pure black — kills the
  // brightness that made the old curve feel oversaturated at the
  // top of the fade band.
  const clean = hex.replace('#', '');
  const r = Math.round(parseInt(clean.slice(0, 2), 16) * 0.45);
  const g = Math.round(parseInt(clean.slice(2, 4), 16) * 0.45);
  const b = Math.round(parseInt(clean.slice(4, 6), 16) * 0.45);
  return `rgb(${r}, ${g}, ${b})`;
}
// POL-16 — full-page sky.  The POL-12/POL-15 top-band (vh-stop)
// curve cut to solid floor by 75vh, which read as "gradient dies a
// screen in".  Stops are now PERCENTAGES of the backdrop's own
// height (the `absolute inset-0` layer grows with page content), so
// the time-of-day palette stretches through the ENTIRE scroll and
// only settles onto the editorial floor at the very bottom.
function pol12PageGradient(colors: [string, string, string]): string {
  const darkMid = pol15Darken(colors[2]);
  return [
    `linear-gradient(180deg,`,
    `  ${colors[0]} 0%,`,
    `  ${colors[1]} 22%,`,
    `  ${colors[2]} 48%,`,
    `  ${darkMid} 78%,`,
    `  ${TODAY_PAGE_FLOOR} 100%`,
    `)`,
  ].join(' ');
}

// ─── Page backdrop ──────────────────────────────────────────────
export function TodayPageBackdrop({ timeOfDay, className }: TodayPageBackdropProps) {
  // POL-11e — Light mode short-circuits below.  Read the mode first
  // but delay the early-return until AFTER every other hook has run,
  // otherwise React's Rules of Hooks fires (`useMemo` calls would
  // appear / disappear as the studio toggles the theme mode).
  const { mode: themeMode } = useThemeMode();

  const now = useMemo(() => new Date(), []);
  const bucket = timeOfDay ?? bucketFromHour(now.getHours());
  const g = gradientFor(bucket);

  // POL-16 — full-page gradient, %-stops across the whole scroll.
  const gradient = pol12PageGradient(g.colors);

  const daySeed = useMemo(() => {
    const start = new Date(now.getFullYear(), 0, 0);
    const diff = now.getTime() - start.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24)) * 2246822519;
  }, [now]);
  // POL-15 — gradient fade region extended to 75vh with an intermediate
  // dark stop at 55vh, so the star region now lives through 45vh — most
  // stars sit in the top half of the fade, then thin out as they cross
  // into the darker mid-tone before disappearing into the floor.
  const starCount = POL12_STAR_COUNTS[bucket];
  const maxYvh = 45;
  const stars = useMemo(
    () => (starCount > 0 ? seededStars(starCount, daySeed, maxYvh) : []),
    [starCount, daySeed],
  );

  const phase = useMemo(() => lunarPhase(now), [now]);

  // POL-11e — Light-mode short-circuit.  Plain near-white canvas
  // (no gradient, no stars, no moon) so Today stays editorial and
  // legible in light mode; cards on top handle their own light-mode
  // surfaces (bg-card + border-border).
  if (themeMode === 'light') {
    return (
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 ${className ?? ''}`}
        style={{ background: '#FAFAFA' }}
      />
    );
  }

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className ?? ''}`}
      style={{ background: gradient }}
    >
      {/* Star layer — each star is its own square-viewBox SVG so the
          shape stays 1:1 no matter how wide the container gets. */}
      {stars.map((star, i) => (
        <svg
          key={i}
          className="absolute"
          width={star.size}
          height={star.size}
          viewBox="0 0 10 10"
          preserveAspectRatio="xMidYMid meet"
          style={{
            left: `calc(${star.x}% - ${star.size / 2}px)`,
            top: `calc(${star.y}vh - ${star.size / 2}px)`,
            opacity: star.opacity,
            animation: `todayPageTwinkle ${star.duration}s ease-in-out ${star.delay}s infinite`,
          }}
        >
          <ShapeSvg shape={star.shape} color={g.starColor} opacity={1} />
        </svg>
      ))}

      {/* Moon — night only, positioned over the top ~15% of the
          viewport, right-aligned so it lands roughly where the old
          hero moon used to sit. */}
      {g.showMoon && (
        <div
          className="absolute"
          style={{ top: '10vh', right: '6vw' }}
        >
          <Moon phase={phase} size={44} />
        </div>
      )}

      <style>
        {`
          @keyframes todayPageTwinkle {
            0%, 100% { opacity: 0.55; transform: scale(1); }
            50%      { opacity: 1;    transform: scale(1.15); }
          }
          @media (prefers-reduced-motion: reduce) {
            [style*="todayPageTwinkle"] {
              animation: none !important;
              opacity: 0.8 !important;
              transform: none !important;
            }
          }
        `}
      </style>
    </div>
  );
}

export default TodayPageBackdrop;
